import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';
import ReferenceTooltip from './ReferenceTooltip';
import './capture.css';
import apiClient from '../../api/client';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';

const OtpVerification = ({ assignment = {}, workflow = {}, branding = {}, onVerified, token }) => {
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const inputRefs = useRef([]);
  const tooltipTriggerRef = useRef(null);
  const sentOtpAssignmentIdRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);
  const welcomeTitle = workflow.branding?.welcomeTitle || 'Welcome, {name}';

  const otpDestination = assignment.sendOtpVia === 'sms' ? assignment.submitterPhone : assignment.submitterEmail;

  const currentTheme = branding.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'aqua', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);

  const sendOtpApi = useCallback(async () => {
    if (!assignment?.id) {
      return;
    }

    try {
      await apiClient.post('/capture/otp/send', { assignmentId: assignment.id });
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to send verification code right now.';
      setError(message);
    } finally {
      setIsOtpSent(true);
    }
  }, [assignment?.id]);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
  }, []);

  const submitOtp = async (code) => {
    if (code.length < 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }

    if (!assignment?.id) {
      setError('Assignment information is missing.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await apiClient.post('/capture/otp/verify', { assignmentId: assignment.id, otp: code });
      onVerified();
    } catch (err) {
      const message = err?.response?.data?.message || 'The verification code is invalid or has expired.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!assignment?.id || sentOtpAssignmentIdRef.current === assignment.id) {
      return;
    }

    sentOtpAssignmentIdRef.current = assignment.id;
    inputRefs.current[0]?.focus();

    const triggerOtpSend = async () => {
      await sendOtpApi();
      startResendCooldown();
    };

    triggerOtpSend();
  }, [assignment?.id, sendOtpApi, startResendCooldown]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleOtpChange = (index, value) => {
    const sanitizedValue = value.replace(/\D/g, '').slice(-1);
    const nextOtp = [...otp];
    nextOtp[index] = sanitizedValue;
    setOtp(nextOtp);
    setError('');

    if (sanitizedValue && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (sanitizedValue && nextOtp.every((digit) => digit)) {
      inputRefs.current[index]?.blur();
      submitOtp(nextOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      const previousOtp = [...otp];
      previousOtp[index - 1] = '';
      setOtp(previousOtp);
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pastedValue = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (!pastedValue) {
      return;
    }

    event.preventDefault();
    const nextOtp = Array(6).fill('');
    pastedValue.split('').forEach((digit, index) => {
      nextOtp[index] = digit;
    });
    setOtp(nextOtp);
    setError('');

    if (pastedValue.length === 6) {
      inputRefs.current[5]?.blur();
      submitOtp(pastedValue);
      return;
    }

    const nextIndex = Math.min(pastedValue.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    setOtp(Array(6).fill(''));
    setError('');

    try {
      await sendOtpApi();
      startResendCooldown();
      inputRefs.current[0]?.focus();
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to resend the verification code.';
      setError(message);
    } finally {
      setIsResending(false);
    }
  };

  if (!isOtpSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base-content text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-theme={currentTheme}
      className="min-h-[100dvh] flex flex-col font-sans transition-colors duration-500 bg-base-100 text-base-content overflow-x-hidden pt-[env(safe-area-inset-top,20px)] capture-container"
    >
      <div className="w-full max-w-lg mx-auto">
        <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} type="before-capture" token={token} />
      </div>

      <div className='px-3 w-full max-w-lg mx-auto flex flex-col'>
        <div className="mb-6">
          <h1 className="text-[20px] font-bold tracking-tight mb-2 text-base-content leading-none custom-line-break">
            {welcomeTitle.replaceAll('{name}', assignment.submitterName || '') || 'Welcome'}
          </h1>
          <div className="flex flex-col gap-1.5 text-[14px]">
            <div className="flex justify-between items-start overflow-visible custom-line-break w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
              <span className="relative inline-block text-right shrink-0">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-primary underline decoration-dashed focus:outline-none custom-line-break"
                >
                  {assignment.customerReferenceNumber}
                </button>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-3 pb-24 w-full max-w-lg mx-auto flex flex-col mt-28">

        <div>
          <h2 className="text-[20px] font-bold text-base-content mb-3">We sent you a code</h2>
          <p className="text-[14px] text-base-content/70 mb-6">
            Please enter the 6-digit code we sent to
            {otpDestination ? <><br /><span className="font-semibold text-base-content">{otpDestination}</span></> : ' you.'}
          </p>

          <div className="flex justify-between gap-1 mb-2" onPaste={handlePaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  inputRefs.current[index] = element;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                disabled={isSubmitting}
                onChange={(event) => handleOtpChange(index, event.target.value)}
                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                className={`h-11 w-11 rounded-xl border text-center text-[18px] font-semibold outline-none transition disabled:opacity-60 ${isDark ? 'border-base-300 bg-base-200 text-base-content' : 'border-base-300 bg-white text-base-content'} focus:border-primary focus:ring-2 focus:ring-primary/20`}
                aria-label={`OTP digit ${index + 1}`}
              />
            ))}
          </div>

          {error ? <p className="mb-3 text-sm text-error text-center">{error}</p> : null}

          <p className="text-[14px] text-base-content/70">
            Didn&apos;t get the code?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0 || !isOtpSent}
              className="font-semibold text-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;

OtpVerification.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string,
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
    sendOtpVia: PropTypes.string,
    submitterEmail: PropTypes.string,
    submitterPhone: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  onVerified: PropTypes.func,
  token: PropTypes.string,
};
